// Calendar, DatePicker and DateRangePicker. Values are store-local calendar days "YYYY-MM-DD"
// (never Date objects): the server interprets them in the store timezone, so there is no UTC drift.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from './cn'
import { Button } from './button'
import { Popover } from './menu'
import { useFieldControl } from './field'
import { addDays, dayLabel, getFormatDefaults, today as todayKey } from '@/lib/format'
import { DATE_PRESETS, resolvePreset, rangeLabel, type DatePreset, type DateRange } from './date-range'
import { useIsMobile } from '@/lib/hooks'

// ─── day-key arithmetic (UTC-noon anchored, timezone-free) ──────────────────────────────
const parse = (k: string) => { const [y, m, d] = k.split('-').map(Number); return { y, m: m - 1, d } }
const key = (y: number, m: number, d: number) => { const t = new Date(Date.UTC(y, m, d)); return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}` }
const weekday = (k: string) => { const { y, m, d } = parse(k); return new Date(Date.UTC(y, m, d)).getUTCDay() }
const monthStart = (k: string) => { const { y, m } = parse(k); return key(y, m, 1) }
const addMonths = (k: string, n: number) => { const { y, m } = parse(k); return key(y, m + n, 1) }
const daysInMonth = (k: string) => { const { y, m } = parse(k); return new Date(Date.UTC(y, m + 1, 0)).getUTCDate() }
const isKey = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)

function firstDayOfWeek(locale: string): number {
  try {
    const l = new Intl.Locale(locale) as Intl.Locale & { getWeekInfo?: () => { firstDay: number }; weekInfo?: { firstDay: number } }
    const info = l.getWeekInfo?.() ?? l.weekInfo
    if (info) return info.firstDay % 7
  } catch { /* old engines */ }
  return /^en(-(US|CA|IN|PH))?$/i.test(locale) || locale === 'en' ? 0 : 1
}

function monthTitle(k: string, locale: string) {
  const { y, m } = parse(k)
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m, 15)))
}
function weekdayNames(locale: string, first: number) {
  const f = new Intl.DateTimeFormat(locale, { weekday: 'narrow', timeZone: 'UTC' })
  const long = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' })
  // 2023-01-01 was a Sunday.
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(Date.UTC(2023, 0, 1 + ((first + i) % 7), 12)); return { short: f.format(d), long: long.format(d) } })
}

export interface CalendarProps {
  mode?: 'single' | 'range'
  /** single: "YYYY-MM-DD" | null; range: { from, to } */
  selected?: string | null
  range?: { from: string | null; to: string | null }
  onSelect?: (day: string) => void
  /** First visible month ("YYYY-MM-01"); uncontrolled by default. */
  month?: string
  onMonthChange?: (m: string) => void
  months?: 1 | 2
  min?: string
  max?: string
  /** Store "today" (defaults to the store timezone's today). */
  today?: string
  locale?: string
  className?: string
}

/** Keyboard-accessible month grid(s): arrows move by day/week, PageUp/PageDown by month, Enter selects. */
export function Calendar({ mode = 'single', selected, range, onSelect, month, onMonthChange, months = 1, min, max, today, locale, className }: CalendarProps) {
  const loc = locale ?? getFormatDefaults().locale
  const t = today ?? todayKey()
  const initial = monthStart(mode === 'range' ? (range?.to ?? range?.from ?? t) : (selected ?? t))
  const [innerMonth, setInnerMonth] = useState(() => (months === 2 && mode === 'range' && range?.to && range.from && monthStart(range.from) !== monthStart(range.to) ? monthStart(range.from) : months === 2 ? addMonths(initial, -1) : initial))
  const shown = month ?? innerMonth
  const setMonth = (m: string) => { setInnerMonth(m); onMonthChange?.(m) }
  const [focusDay, setFocusDay] = useState<string>(selected ?? range?.from ?? t)
  const [hover, setHover] = useState<string | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const first = firstDayOfWeek(loc)
  const names = useMemo(() => weekdayNames(loc, first), [loc, first])
  const disabled = (k: string) => (min !== undefined && k < min) || (max !== undefined && k > max)

  const moveFocus = (k: string) => {
    if (disabled(k)) return
    setFocusDay(k)
    const lastShown = addMonths(shown, months - 1)
    if (monthStart(k) < shown) setMonth(monthStart(k))
    else if (monthStart(k) > lastShown) setMonth(addMonths(monthStart(k), -(months - 1)))
    requestAnimationFrame(() => gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${k}"]`)?.focus())
  }
  const onKey = (e: KeyboardEvent) => {
    const map: Record<string, () => string> = {
      ArrowLeft: () => addDays(focusDay, -1), ArrowRight: () => addDays(focusDay, 1),
      ArrowUp: () => addDays(focusDay, -7), ArrowDown: () => addDays(focusDay, 7),
      PageUp: () => { const { d } = parse(focusDay); const m = addMonths(focusDay, e.shiftKey ? -12 : -1); return key(parse(m).y, parse(m).m, Math.min(d, daysInMonth(m))) },
      PageDown: () => { const { d } = parse(focusDay); const m = addMonths(focusDay, e.shiftKey ? 12 : 1); return key(parse(m).y, parse(m).m, Math.min(d, daysInMonth(m))) },
      Home: () => addDays(focusDay, -((weekday(focusDay) - first + 7) % 7)),
      End: () => addDays(focusDay, 6 - ((weekday(focusDay) - first + 7) % 7)),
    }
    if (map[e.key]) { e.preventDefault(); moveFocus(map[e.key]()) }
  }

  const rangeFrom = range?.from ?? null
  const rangeTo = range?.to ?? (rangeFrom && hover && hover >= rangeFrom ? hover : null)

  return (
    <div ref={gridRef} className={cn('flex gap-6', className)} onKeyDown={onKey}>
      {Array.from({ length: months }, (_, mi) => {
        const m = addMonths(shown, mi)
        const lead = (weekday(m) - first + 7) % 7
        const count = daysInMonth(m)
        const cells: Array<string | null> = [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => key(parse(m).y, parse(m).m, i + 1))]
        while (cells.length % 7) cells.push(null)
        return (
          <div key={m} className="flex w-[252px] flex-col gap-2">
            <div className="flex h-8 items-center justify-between">
              {mi === 0 ? <Button variant="ghost" size="sm" icon={<ChevronLeft />} aria-label="Previous month" onClick={() => setMonth(addMonths(shown, -1))} /> : <span className="size-7" />}
              <span className="text-base font-semibold text-text" aria-live="polite">{monthTitle(m, loc)}</span>
              {mi === months - 1 ? <Button variant="ghost" size="sm" icon={<ChevronRight />} aria-label="Next month" onClick={() => setMonth(addMonths(shown, 1))} /> : <span className="size-7" />}
            </div>
            <div role="grid" aria-label={monthTitle(m, loc)} className="grid grid-cols-7 gap-y-1" onPointerLeave={() => setHover(null)}>
              {names.map((n, i) => <div key={i} role="columnheader" aria-label={n.long} className="grid h-7 place-items-center text-xs font-medium text-text-subtle">{n.short}</div>)}
              {cells.map((k, i) => {
                if (!k) return <div key={`e${i}`} role="gridcell" />
                const isSel = mode === 'single' ? k === selected : k === rangeFrom || k === rangeTo
                const inRange = mode === 'range' && rangeFrom && rangeTo && k > rangeFrom && k < rangeTo
                const isStart = mode === 'range' && k === rangeFrom && !!rangeTo && rangeTo !== rangeFrom
                const isEnd = mode === 'range' && k === rangeTo && !!rangeFrom && rangeTo !== rangeFrom
                const dis = disabled(k)
                return (
                  <div key={k} role="gridcell" className={cn('relative flex h-9 items-center justify-center',
                    inRange && 'bg-primary-soft', isStart && 'bg-gradient-to-r from-transparent from-50% to-primary-soft to-50%', isEnd && 'bg-gradient-to-l from-transparent from-50% to-primary-soft to-50%')}>
                    <button
                      type="button"
                      data-day={k}
                      tabIndex={k === focusDay ? 0 : -1}
                      disabled={dis}
                      aria-selected={isSel || undefined}
                      aria-current={k === t ? 'date' : undefined}
                      aria-label={dayLabel(k, 'long', loc)}
                      onClick={() => { setFocusDay(k); onSelect?.(k) }}
                      onPointerEnter={() => setHover(k)}
                      onFocus={() => setFocusDay(k)}
                      className={cn(
                        'relative grid size-9 place-items-center rounded-control text-base tabular transition-colors duration-100 outline-none',
                        'hover:bg-surface-active focus-visible:ring-2 focus-visible:ring-focus disabled:pointer-events-none disabled:text-text-subtle disabled:opacity-40',
                        inRange && 'rounded-none hover:bg-primary/15',
                        isSel && 'bg-primary font-medium text-primary-foreground hover:bg-primary-hover',
                        k === t && !isSel && 'font-semibold text-primary-strong',
                      )}
                    >
                      {parse(k).d}
                      {k === t && !isSel && <span className="absolute bottom-1 size-1 rounded-full bg-primary" aria-hidden />}
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── DatePicker ─────────────────────────────────────────────────────────────────────────

export interface DatePickerProps {
  value: string | null
  onChange: (day: string | null) => void
  placeholder?: string
  min?: string
  max?: string
  clearable?: boolean
  disabled?: boolean
  invalid?: boolean
  id?: string
  className?: string
}

/** Single day picker: `<DatePicker value={startsOn} onChange={setStartsOn} min={today()} />` ("YYYY-MM-DD"). */
export function DatePicker({ value, onChange, placeholder = 'Pick a date', min, max, clearable = true, disabled, invalid, id, className }: DatePickerProps) {
  const f = useFieldControl()
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen} className="w-auto p-3"
      trigger={
        <button type="button" id={id ?? f?.id} disabled={disabled ?? f?.disabled} aria-invalid={(invalid ?? f?.invalid) || undefined} aria-describedby={f?.describedBy}
          className={cn('group flex h-8 w-full min-w-0 items-center gap-2 rounded-control border border-input bg-surface px-3 text-left text-base shadow-control outline-none',
            'hover:border-border-strong focus-visible:border-focus focus-visible:ring-3 focus-visible:ring-focus/20 data-[state=open]:border-focus data-[state=open]:ring-3 data-[state=open]:ring-focus/20',
            'aria-[invalid=true]:border-danger disabled:cursor-not-allowed disabled:bg-surface-sunken', className)}>
          <CalendarDays className="size-4 shrink-0 text-text-subtle" />
          <span className={cn('flex-1 truncate', !value && 'text-text-subtle')}>{isKey(value) ? dayLabel(value, 'medium') : placeholder}</span>
          {clearable && value && !disabled && (
            <span role="button" tabIndex={-1} aria-label="Clear date" onPointerDown={e => { e.preventDefault(); e.stopPropagation(); onChange(null) }}
              className="grid size-5 place-items-center rounded text-text-subtle hover:bg-surface-active hover:text-text"><X className="size-3.5" /></span>
          )}
        </button>
      }>
      <Calendar selected={isKey(value) ? value : null} onSelect={d => { onChange(d); setOpen(false) }} min={min} max={max} />
    </Popover>
  )
}

// ─── DateRangePicker ────────────────────────────────────────────────────────────────────

export { DATE_PRESETS, resolvePreset, previousPeriod, rangeLabel, type DatePreset, type DateRange } from './date-range'

export interface DateRangePickerProps {
  value: DateRange | null
  onChange: (r: DateRange | null) => void
  /** Allow "Any time" (null). */
  clearable?: boolean
  min?: string
  max?: string
  presets?: Array<Exclude<DatePreset, 'custom'>>
  align?: 'start' | 'end'
  size?: 'sm' | 'md'
  className?: string
}

/**
 * Range picker with presets (today, 7/30/90 days, month, year…) and a custom two-month calendar.
 * `<DateRangePicker value={range} onChange={setRange} />` — `resolvePreset('last_30_days')` for defaults.
 */
export function DateRangePicker({ value, onChange, clearable, min, max = todayKey(), presets, align = 'start', size = 'md', className }: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<{ from: string | null; to: string | null; preset?: DatePreset }>({ from: value?.from ?? null, to: value?.to ?? null, preset: value?.preset })
  const mobile = useIsMobile()
  useEffect(() => { if (open) setDraft({ from: value?.from ?? null, to: value?.to ?? null, preset: value?.preset }) }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const list = presets ? DATE_PRESETS.filter(p => presets.includes(p.id)) : DATE_PRESETS
  const pick = (d: string) => {
    if (!draft.from || draft.to) setDraft({ from: d, to: null, preset: 'custom' })
    else if (d < draft.from) setDraft({ from: d, to: draft.from, preset: 'custom' })
    else setDraft({ from: draft.from, to: d, preset: 'custom' })
  }
  const apply = () => {
    if (draft.from) { onChange({ from: draft.from, to: draft.to ?? draft.from, preset: draft.preset ?? 'custom' }); setOpen(false) }
  }
  return (
    <Popover open={open} onOpenChange={setOpen} align={align} className="w-auto p-0"
      trigger={
        <Button size={size} icon={<CalendarDays />} className={cn('tabular', className)} aria-label={`Date range: ${rangeLabel(value)}`}>
          {rangeLabel(value)}
        </Button>
      }>
      <div className="flex flex-col sm:flex-row">
        <div className="flex gap-1 overflow-x-auto border-b border-border p-2 sm:w-40 sm:flex-col sm:overflow-visible sm:border-r sm:border-b-0">
          {clearable && (
            <button type="button" onClick={() => { onChange(null); setOpen(false) }}
              className={cn('h-8 shrink-0 rounded-[6px] px-3 text-left text-base whitespace-nowrap hover:bg-surface-active', !value && 'bg-surface-active font-medium')}>Any time</button>
          )}
          {list.map(p => {
            const active = draft.preset === p.id
            return (
              <button key={p.id} type="button"
                onClick={() => { const r = resolvePreset(p.id); setDraft(r) }}
                onDoubleClick={() => { onChange(resolvePreset(p.id)); setOpen(false) }}
                className={cn('h-8 shrink-0 rounded-[6px] px-3 text-left text-base whitespace-nowrap hover:bg-surface-active', active && 'bg-primary-soft font-medium text-primary-strong hover:bg-primary-soft')}>
                {p.label}
              </button>
            )
          })}
        </div>
        <div className="flex flex-col">
          <div className="p-3">
            <Calendar key={`${open}`} mode="range" range={draft} onSelect={pick} months={mobile ? 1 : 2} min={min} max={max} />
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-3">
            <span className="text-sm text-text-muted tabular">
              {draft.from ? rangeLabel({ from: draft.from, to: draft.to ?? draft.from, preset: 'custom' }) : 'Select a start date'}
            </span>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" variant="primary" disabled={!draft.from} onClick={apply}>Apply</Button>
            </div>
          </div>
        </div>
      </div>
    </Popover>
  )
}
